/* eslint-disable max-len */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const SubLedgerTable = ({ tableData, searchParams, formatMMDDate}) => {
    const navigate = useNavigate();
    const [currentLedger, setCurrentLedger] = useState(null);

    /**
     * The `ledgerDrillDownhandler` function handles drill-down functionality for ledger details in a React
     * application, including setting parameters, constructing a URL, making an API call, and updating
     * state. 
     * @returns The function `ledgerDrillDownhandler` returns nothing (`undefined`) if the condition
     * `!isStudentDetails` is met or if `studentID` is truthy.
     */
    const ledgerDrillDownhandler = async (
        ledgerName, 
        isStudentDetails = true, 
        ledgerId, 
        ayID, 
        classID, 
        sectionID, 
        studentID, 
        isTransaction, 
        parentLedgerName = null,
        level,
        studentUuid
    ) => {
        if (!isStudentDetails && !isTransaction ) {
            return; 
        } else {
            // setPageNumber(1) for infinte scroll
            let params = Object.fromEntries(searchParams);
            if (studentID || isTransaction){
                params.type = 'transaction';
            } else {         
                params.type = 'drill_down';
            }
            ledgerId ? params.ledger_id = ledgerId :''
            ayID ? params.ay_id = ayID : ''
            classID ? params.class_id = classID : ''
            sectionID ? params.section_id = sectionID : ''
            studentID ? params.student_id = studentID : ''
            studentUuid ? params.student_uuid = studentUuid : ''
            let theLedgerName
            if(studentID){
                theLedgerName = ledgerName+'['+studentID+']'+' Ledger Extract'
            } else if (isTransaction){
                theLedgerName = ledgerName +' Ledger Extract'
            }else{
                theLedgerName = ledgerName
            }
            const queryString = new URLSearchParams(params).toString();
            let url = ''
            url = `${location.pathname}/${encodeURIComponent(theLedgerName)}${`?${queryString}`}`;
            navigate(url);
            if (!studentID || !isTransaction){   
                setCurrentLedger({ ledgerName, isStudentDetails, ledgerId, ayID, classID, sectionID });
            }
        }
    };

    /**
     * The `grandTotalHandler` function constructs a query string from search parameters and navigates to a
     * new URL with the encoded ledger name and query string.
     */
    const grandTotalHandler = () => {
        let params = Object.fromEntries(searchParams);
        params.type = 'transaction';
        params.grand_total = true;
        let theLedgerName = 'Ledger Extract'
        const queryString = new URLSearchParams(params).toString();
        navigate(`${location.pathname}/${encodeURIComponent(theLedgerName)}${`?${queryString}`}`);
    };

    /* The `useEffect` hook in the code snippet is used to perform side effects in function components in
    React. In this specific case: */
    useEffect(() => {
        // Trigger state update to force re-render
    }, [currentLedger]);

    /* The below code is a JavaScript React function called `renderTableRows` that generates table rows
    based on the provided data. It iterates over the `data` array and for each `ledger` object, it
    creates a table row with specific styling and content. */
    const renderTableRows = (data, level = 1,  parentLedgerName = null) => {
        //if the page is loaded too fast and data is still empty then it will break, so the below failsafe
        if (!Array.isArray(data)) {
            return null;
        }
        return searchParams.get('type') !== 'transaction' && data?.map((ledger, index) => {
            //with each level the left padding will be increased to show hirerachy 
            const leftPadding = level === 1 ? '0.75rem' : `${level * 20}px`; // Adjust padding based on level
            const currentParentLevel1Name = level === 1 ? ledger?.name : parentLedgerName;
            return (
                <React.Fragment key={index + ledger?.name}>
                    <tr className={`flex ${(searchParams.get('type') === 'drill_down' || ledger?.is_student_details || ledger?.is_transaction ) ? 'cursor-pointer' : ''}`} id={'row'+index} onClick={() => {
                        if(searchParams.get('type') !== 'drill_down'){
                            ledgerDrillDownhandler(ledger?.name, 
                                ledger?.is_student_details, 
                                ledger?.ledger_id, 
                                undefined, 
                                undefined, 
                                undefined, 
                                undefined,
                                ledger?.is_transaction, 
                                currentParentLevel1Name,
                                level, 
                                undefined
                            );
                        } else {
                            ledgerDrillDownhandler(ledger?.name, 
                                true, 
                                ledger?.ledger_id, 
                                ledger?.academic_year_id, 
                                ledger?.class_id,
                                ledger?.section_id,
                                ledger?.student_id,
                                ledger?.is_transaction, 
                                currentParentLevel1Name,
                                level,
                                ledger?.student_uuid
                            );
                        }
                    }}>
                        <td
                            className={`flex-1 py-2 pr-3 break-all`}
                            style={{ paddingLeft: leftPadding }}
                        >
                            <span id={ledger?.name + '0'} className={`${level === 1 && ledger?.has_child ? 'font-semibold' : ''} ${ledger?.has_child ? 'border-b border-[#000000]' : ''}`}>
                                {ledger?.student_id ? ledger?.name+'['+ledger?.student_id+']' : ledger?.name}
                            </span>
                        </td>
                        <td className={`flex-none pl-3 py-2 pr-3 text-end ${searchParams.get('type') === 'drill_down' ? 'min-w-[158.5px]' : 'min-w-[222.5px]'}`}>
                            <span id={'debit_' + ledger?.name + '0'} className={`${level === 1 && ledger?.has_child ? 'font-semibold' : ''} ${ledger?.has_child ? 'border-b border-[#000000]' : ''}`}>
                                {searchParams.get('type') !== 'drill_down' ? ledger?.balance_type === 'dr' ? Math.abs(parseFloat(ledger?.balance)).toFixed(2) : '' : Math.abs(parseFloat(ledger?.total_amount_dr)).toFixed(2)}
                            </span>
                        </td>
                        <td className={`flex-none pl-3 py-2 pr-3 text-end ${searchParams.get('type') === 'drill_down' ? 'min-w-[158.5px]' : 'min-w-[222.5px]'}`}>
                            <span id={'credit_' + ledger?.name + '0'} className={`${level === 1 && ledger?.has_child ? 'font-semibold' : ''} ${ledger?.has_child ? 'border-b border-[#000000]' : ''}`}>
                                {searchParams.get('type') !== 'drill_down' ? ledger?.balance_type === 'cr' ? Math.abs(parseFloat(ledger?.balance)).toFixed(2) : '' : Math.abs(parseFloat(ledger?.total_amount_cr)).toFixed(2)}
                            </span>
                        </td>
                        {searchParams.get('type') === 'drill_down' && <td className="flex-none pl-3 py-2 pr-3 text-end min-w-[220px]">
                            <span id={'closing_balance_' + ledger?.name + '0'} className={`${level === 1 && ledger?.has_child ? 'font-semibold' : ''} ${ledger?.has_child ? 'border-b border-[#000000]' : ''}`}>
                                {Math.abs(parseFloat(ledger?.total_amount)).toFixed(2)}
                            </span>
                        </td>}
                    </tr>
                    {ledger?.has_child && ledger?.items?.length > 0 && (
                        <tr className="">
                            <td colSpan={4} className="p-0">
                                <table className="w-full">
                                    <tbody>
                                        {renderTableRows(ledger?.items, level + 1, currentParentLevel1Name)}
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    )}
                </React.Fragment>
            );
        });
    };

    return ( searchParams.get('type') !== 'transaction' && 
        <>
            <div className="mb-10 overflow-auto scroll-bar border border-[#C2DCEF]">
                <div className="min-h-[calc(100vh-400px)]">
                    <table aria-label="Outside Table" className="w-full table-auto relative">
                        <thead>
                            <tr className="balanc-sheet font-semibold text-[#106EB4] h-[60px] bg-[#EEF7FE] flex">
                                <th id="particulars-header" className="flex-1 pl-[10px] flex items-center min-w-[380px] h-[60px] border-b border-[#C2DCEF] ">
                                    Particulars
                                </th>
                                <th className={`flex-none border-r border-l h-[60px]  border-b border-[#C2DCEF] ${searchParams.get('type') === 'drill_down' ? 'min-w-[320px]' : 'min-w-[445px]'}`}>
                                    <div id={searchParams.get('type') === 'drill_down' ? 'transactions-header' : 'closing-balance-header'} 
                                        className="border-b border-[#C2DCEF] h-[30px] flex justify-center items-center">{searchParams.get('type') === 'drill_down' ? 'Transactions' : 'Closing Balance'}</div>
                                    <div className="flex w-full h-[30px]">
                                        <div id="debit-header" className="flex-1 pl-3 pr-3 border-r border-[#C2DCEF] text-center flex justify-end items-center">Debit</div>
                                        <div id="credit-header" className="flex-1 pl-3 pr-3 text-center flex justify-end items-center">Credit</div>
                                    </div>
                                </th>
                                {searchParams.get('type') === 'drill_down' && <th id="closing-balance-header" className="flex-none pr-[18px] h-[60px] border-b border-[#C2DCEF] flex justify-end items-center min-w-[220px]">
                                    <span>Closing Balance</span>
                                </th>}
                            </tr>
                        </thead>
                        <tbody className="mt-[12px]">
                            {(searchParams.get('main_group') === 'Assets' || searchParams.get('main_group') === 'Liabilities') && <tr>
                                <td className="flex justify-between pl-3 py-2 pr-3">
                                    <div>Opening balance as on <span id={'opening_balance_date'}>{formatMMDDate(tableData?.start_date)}</span></div>
                                    <div id={'opening_balance_amount'}>{Math.abs(parseFloat(tableData?.opening_balance)).toFixed(2)}</div>
                                </td>
                            </tr>}
                            {tableData && renderTableRows(searchParams.get('type') !== 'drill_down' ? tableData : tableData.line_item)}
                        </tbody>
                    </table>
                </div>
                {searchParams.get('type') === 'drill_down' && (
                    <div className="h-[36px] font-semibold bg-[#EEF7FE] flex justify-between items-center">
                        <div className="min-w-[380px] h-[36px] flex justify-start items-center">
                            <div className="group relative flex justify-start items-center">
                                <p
                                    className="pl-[10px] bg-[#EEF7FE] cursor-pointer hover:underline"
                                    id="Grand_Total"
                                    onClick={() => {grandTotalHandler()}}
                                >
                                    Grand Total
                                </p>
                                <img
                                    src="/images/view_ledger_extract.svg"
                                    alt="icon"
                                    className="ml-2 scale-0 group-hover:scale-100"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end items-center">
                            <p className="flex-none flex justify-end pl-3 py-2 pr-3 items-center min-w-[158.5px] h-[36px] bg-[#EEF7FE]" id={'Grand_Total_Debit'}>{Math.abs(parseFloat(tableData?.grand_total_dr)).toFixed(2)}</p>
                            <p className="flex-none flex justify-end pl-3 py-2 pr-3 items-center min-w-[158.5px] h-[36px] bg-[#EEF7FE]" id={'Grand_Total_Credit'}>{Math.abs(parseFloat(tableData?.grand_total_cr)).toFixed(2)}</p>
                            <p className="flex-none pr-[18px] pl-3 py-2 2xl:pr-3 flex justify-end items-center min-w-[220px] h-[36px] bg-[#EEF7FE]" id={'grand_total_closing_balance'}>{Math.abs(parseFloat(tableData?.grand_total_closing_balance)).toFixed(2)}</p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SubLedgerTable;
